import { type ComponentProps, type ReactNode } from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { twMerge } from 'tailwind-merge'
import { Building } from 'lucide-react'

/**
 * A workspace's tile: one glyph on a hue from the category ramp. It names a workspace the way a
 * face names a person, and it is not a logo: a workspace exists before it has one, and every
 * workspace carries the same building glyph, so the hue is what tells two apart.
 *
 * A fill, so the glyph takes the fill's own ink, which holds in both themes: white on purple and
 * blue, and the dark on-teal step on teal, which is light.
 */
const TONES = {
  purple: 'bg-purple text-on-purple',
  info: 'bg-info text-on-info',
  teal: 'bg-teal text-on-teal',
}

/**
 * Five sizes, each with its glyph step. sm is the one a menu's lead lane is built around: 20 wide,
 * so it fills the lane a 14px icon centres in and a menu holding both keeps one column. xs is the
 * badge on a face; below 11 the glyph stops reading as a building, so it is drawn at 7, under the
 * icon scale. lg and xs take the corner of the size they stand for, scaled to their own.
 */
const SIZES = {
  xs: 'size-[11px] rounded-[calc(var(--radius-sm)*11/20)] [&>svg]:size-[7px]',
  sm: 'size-5 rounded-[var(--radius-sm)] [&>svg]:size-3',
  md: 'size-[26px] rounded-[var(--radius-md)] [&>svg]:size-3.5',
  lg: 'size-8 rounded-[calc(var(--radius-lg)*32/36)] [&>svg]:size-4',
  xl: 'size-9 rounded-[var(--radius-lg)] [&>svg]:size-4',
}

const markVariants = cva('corner-squircle grid flex-none place-items-center', {
  variants: { tone: TONES, size: SIZES },
  defaultVariants: { tone: 'purple', size: 'md' },
})

export const MARK_TONES = Object.keys(TONES) as Array<keyof typeof TONES>
export const MARK_SIZES = Object.keys(SIZES) as Array<keyof typeof SIZES>

interface MarkProps extends Omit<ComponentProps<'span'>, 'children'>, VariantProps<typeof markVariants> {
  /** The glyph. Every workspace carries the building, so leave it unset unless the tile names something else. */
  children?: ReactNode
}

function Mark({ tone, size, className, children, ...props }: MarkProps) {
  return (
    <span data-slot="mark" aria-hidden className={twMerge(markVariants({ tone, size }), className)} {...props}>
      {children ?? <Building />}
    </span>
  )
}

export { Mark, type MarkProps }
